<template>
  <div
    :class="`tw-relative w-100 ${
      disabled ? 'tw-text-[#B7B7B7] tw-pointer-events-none' : ''
    }`"
  >
    <BDropdown
      :id="props.id"
      v-model="isDropdownOpen"
      :class="[
        'tw-relative baseDropDown',
        'focusDrop ',
        className,
        validation.$error ? 'invalid' : '',
      ]"
      block
      menu-class="w-100"
      @keydown.down="moveSelectionDown(1)"
      @keydown.up="moveSelectionUp(-1)"
      @keydown="selectItem"
      @show="showDropdow"
    >
      <slot>
        <div v-if="useVisualScroll">
          <RecycleScroller
            v-slot="{ item, index }"
            class="scroller"
            :items="props.data"
            :item-size="56"
            :key-field="'id'"
          >
            <BaseDropDownItem
              :id="item.id.toString()"
              :key="item.id.toString()"
              :data-cy="`base-dropdown-item-${item.id.toString()}`"
              :text="item.name || ''"
              :class-name="`normal-dropdown-container 'focusDrop' ${
                activeItem === index ? 'activeItem' : ''
              }`"
              @click="handleSelect(item.id, item)"
            />
          </RecycleScroller>
        </div>
        <!-- Normal -->
        <div v-else>
          <div>
            <BaseDropDownItem
              v-for="option in props.data"
              :id="option.id.toString()"
              :key="option.id.toString()"
              :data-cy="`base-dropdown-item-${option.id.toString()}`"
              :text="option.name"
              :class-name="`normal-dropdown-container  'focusDrop' ${
                props.modelValue === option.id ? 'activeItem' : ''
              } ${option.color ? `tw-text-[${option.color}]` : ''} `"
              :color-text-hover="option.color"
              @click="handleSelect(option.id, option)"
            />
          </div>
        </div>
      </slot>
      <template #button-content>
        <slot name="button-content">
          <div class="tw-flex tw-justify-between tw-items-center">
            <div class="tw-flex tw-items-center tw-w-[95%]">
              <img
                v-if="
                  props.data.find((item) => item.id === modelValue)?.iconSrc ||
                  props.data[0]?.iconSrc
                "
                alt=""
                class="tw-mr-[5px] tw-w-[20px] tw-h-[20px] tw-object-contain"
                :src="
                  props.data.find((item) => item.id === modelValue)?.iconSrc ||
                  props.data[0]?.iconSrc ||
                  ''
                "
              />
              <span
                v-if="
                  props.data?.[0] &&
                  props.data.some((item) => item.id === modelValue)
                "
                :class="` ${
                  disabled ? 'tw-text-[#B7B7B7]' : 'tw-text-[#231F20]'
                } tw-truncate text-dropdown ${classTextName} ${
                  props.data.find((item) => item.id === modelValue)?.color
                    ? `tw-text-[${
                        props.data.find((item) => item.id === modelValue)?.color
                      }]`
                    : ''
                }`"
                :data-cy="dataCyId"
                >{{
                  props.data.find((item) => item.id === modelValue)?.name
                }}</span
              >
              <span
                v-else
                :data-cy="dataCyId"
                :class="[customSelectContainer]"
                >{{ placeholder }}</span
              >
            </div>
            <ArrowIconDropdown class="right-arrow" />
          </div>
        </slot>
      </template>
    </BDropdown>
    <b-row
      v-if="validation?.$errors?.[0]?.$message && showValidate"
      class="tw-items-center"
    >
      <!-- @slot Slot for the feedback, error text -->
      <slot name="feedback">
        <div :class="`${validClass} tw-ml-[15px]`">
          <b-form-invalid-feedback :class="{ 'is-invalid': validation.$error }">
            <VRuntimeTemplate :template="validation?.$errors?.[0]?.$message" />
          </b-form-invalid-feedback>
        </div>
      </slot>
    </b-row>
  </div>
</template>
<script lang="ts" setup>
import { PropType, onMounted, ref, watch } from "vue";
import BaseDropDownItem from "@/components/common/form/baseDropDownItem.vue";
import { IDropDownData } from "@/interfaces/common/IDropDown";
import ArrowIcon from "@/components/svgs/arrowIcon.vue";
import store from "@/store";
import { OVERLAY_KEYBOARD } from "@/utils/constants";
import { RecycleScroller } from "vue-virtual-scroller";
import ArrowIconDropdown from "@/components/svgs/arrowIconDropdown.vue";
import VRuntimeTemplate from "vue3-runtime-template";

const props = defineProps({
  /** The model and value of the input */
  modelValue: {
    type: [Array, Number, String, Object, null] as PropType<unknown>,
    required: true,
  },
  /** Id of input */
  id: {
    type: String,
    required: true,
  },
  dataCyId: {
    type: String,
    required: false,
    default: "",
  },
  /** CSS class name */
  className: {
    type: String,
    default: "",
  },
  classTextName: {
    type: String,
    default: "",
  },
  /** Placeholder text for the input */
  placeholder: {
    type: String,
    default: "",
  },
  /** Data of dropdown options */
  data: {
    type: Array as PropType<IDropDownData[]>,
    required: true,
  },
  /** Description text for the input */
  description: {
    type: String,
    default: "",
  },
  /** Vue Validation object */
  validation: {
    type: Object,
    default: () => ({}),
  },
  customSelectContainer: {
    type: String,
    required: false,
    default: "",
  },
  useVisualScroll: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  isOptionButtonDropdown: {
    type: Boolean,
    required: false,
    default: false,
  },
  bgDropdown: {
    type: String,
    required: false,
    default: "#eff0f180",
  },
  textOptionButtonDropdown: {
    type: String,
    required: false,
    default: "",
  },
  showValidate: {
    type: Boolean,
    required: false,
    default: false,
  },
  validClass: { type: String, required: false, default: "" },
});
const isDropdownOpen = ref(false);
const activeItem = ref(0);
const currentValue = ref<IDropDownData>({} as IDropDownData);

onMounted(() => {
  const matchingOption = props.data.find(
    (item) => item.name === props.modelValue,
  );
  // If a matching option is found, assign its value to currentValue
  if (matchingOption) {
    currentValue.value = matchingOption;
  }
});
const emit = defineEmits([
  "update:modelValue",
  "update:openDropdown",
  "onClickBtnDropdown",
]);

const handleSelect = (optionId, optionData?) => {
  currentValue.value = optionId;
  setTimeout(() => {
    store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
  }, 1000);
  emit("update:modelValue", currentValue.value, optionData);
};
const showDropdow = () => {
  store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.DROPDOWN); //
};
const selectItem = (event) => {
  if (event.key == "ArrowDown") {
    isDropdownOpen.value = true;
    store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.DROPDOWN);
  }
  if (event.key == "Enter") {
    let id: any = props.data[activeItem.value].id;
    handleSelect(id);
    isDropdownOpen.value = false; // close dropdow when select value
    setTimeout(() => {
      store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
    }, 1000);
  }
};
const moveSelectionDown = (step) => {
  if (props.data.length - 1 == activeItem.value) {
    activeItem.value = props.data.length - 1;
  } else if (activeItem.value < props.data.length) {
    activeItem.value += step;
    const elements = document.getElementsByClassName("show");
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      element.scrollTop += 61;
    }
  }
};
const moveSelectionUp = (step) => {
  if (activeItem.value >= 1) {
    activeItem.value += step;
    const elements = document.getElementsByClassName("show");
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      element.scrollTop -= 61;
    }
  }
};

const handleClickBtnDropdown = () => {
  emit("onClickBtnDropdown");
};

watch(isDropdownOpen, (newValue, oldValue) => {
  emit("update:openDropdown", isDropdownOpen.value);
  if (!newValue && oldValue) {
    setTimeout(() => {
      store.commit("keyBoard/SET_ENTER", OVERLAY_KEYBOARD.MODAL);
    }, 1000);
  }
});
</script>
<style lang="scss">
.dropdown-menu {
  &.show {
    padding: 0;
  }
}

.activeItem {
  background-color: #eff0f1 !important;
  border: 1px solid #eff0f1;
}
.full-width-dropdown {
  width: calc(100% - 30px) !important;
}
.baseDropDown {
  &.is-invalid-input {
    border: 1px solid #f7553c;
    background: #fee5e2;
    border-radius: 6px;

    &::after {
      content: "";
    }
  }
  &.is-api-error {
    .btn-secondary {
      border: 1px solid #f7553c !important;
    }
  }
}
.baseDropDown {
  &.invalid {
    .btn-secondary {
      background-color: #fee5e2 !important;
      border-color: #f7553c !important;
    }
  }
  .btn-secondary {
    background-color: v-bind(bgDropdown) !important;
    width: 100%;
    text-align: left;
    padding: 11px 20px;
    color: #231f20;
    font-size: 13px;
    line-height: 1.5;
    // background: transparent;
    border: none;
    box-shadow: none;
    .placeholder {
      color: #b7b7b7;
      background-color: transparent !important;
      opacity: 1;
    }
    &:hover,
    &:focus,
    &:active,
    &:active:focus {
      color: #b7b7b7;
      font-size: 13px;
      background: transparent;
      border: none;
      // box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
    }
    &::after {
      display: none;
    }
  }
  .form-control {
    border-radius: 6px;
    border: 1px solid var(--ui-rule, #eff0f1);
    background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
    padding: 0;
    height: 58px;
    color: #231f20;
    font-size: 13px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    &::placeholder {
      color: #b7b7b7;
    }
  }
  input.form-control {
    border-radius: 6px;
    border: 1px solid var(--ui-rule, #eff0f1);
    background: var(--ui-rule-50, rgba(239, 240, 241, 0.5));
    padding: 18px 20px;
    height: 58px;
    color: #231f20;
    font-size: 13px;
    font-family: Poppins;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    &::placeholder {
      color: #b7b7b7;
    }
  }
  .dropdown-toggle.show .right-arrow {
    transition: transform 0.3s ease;
    transform: rotate(180deg);
    height: 12px;
  }
  .dropdown-toggle .right-arrow {
    transition: transform 0.3s ease;
    transform: rotate(0deg);
    height: 12px;
  }
  .normal-dropdown-container {
    width: 100%;

    .dropdown-toggle::after {
      display: none;
    }
  }
  &.gray-dropdown-container {
    width: 100%;

    &.is-inciden-event-table {
      .btn-secondary {
        padding: 12px;
      }
      &.High {
        .text-dropdown {
          color: #f7553c;
        }
      }
      &.Low {
        .text-dropdown {
          color: #57b755;
        }
      }
      &.Medium {
        .text-dropdown {
          color: #fe9a05;
        }
      }
    }
    .btn-secondary {
      width: 100%;
      text-align: left;
      padding: 18px 20px;

      color: #b7b7b7;
      font-size: 13px;
      line-height: 1.5;

      border: 1px solid #eff0f1;
      box-shadow: none;
      &:hover,
      &:focus,
      &:active,
      &:active:focus {
        color: #b7b7b7;
        font-size: 13px;
        background-color: #eff0f180;
        border: 1px solid #eff0f1;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
      }
    }

    .dropdown-toggle::after {
      display: none;
    }
    &.tw-text-normal {
      .btn {
        color: #231f20;
      }
    }
  }
  &.gray-dropdown-container-wizard {
    width: 100%;

    &.is-inciden-event-table {
      .btn-secondary {
        padding: 12px;
      }
      &.High {
        .text-dropdown {
          color: #f7553c;
        }
      }
      &.Low {
        .text-dropdown {
          color: #57b755;
        }
      }
      &.Medium {
        .text-dropdown {
          color: #fe9a05;
        }
      }
    }
    .btn-secondary {
      width: 100%;
      text-align: left;
      padding: 0px 20px !important;
      border: 1px solid #eff0f1;

      color: #b7b7b7;
      font-size: 13px;
      line-height: 1.5;
      box-shadow: none;
      &:hover,
      &:focus,
      &:active,
      &:active:focus {
        color: #b7b7b7;
        font-size: 13px;
        border: 1px solid #eff0f1;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
      }
    }

    .dropdown-toggle::after {
      display: none;
    }
    &.tw-text-normal {
      .btn {
        color: #231f20;
      }
    }
  }
}
.placeholder-container {
  line-height: normal;
  border-left: 1px solid;
  padding-left: 6px;
}

.dropdown-toggle.show .right-arrow {
  transition: transform 0.3s ease;
  transform: rotate(180deg);
  height: 12px;
}

.dropdown-toggle .right-arrow {
  transition: transform 0.3s ease;
  transform: rotate(0deg);
  height: 12px;
}

.normal-dropdown-container {
  width: 100%;
  .btn-secondary {
    width: 100%;
    text-align: left;
    padding: 19px 25px;
    color: #211f58;
    font-size: 15px;
    line-height: 1.5;
    font-weight: 500;
    background-color: #fff;
    border: 1px solid #eff0f1;
    box-shadow: none;
    &:focus,
    &:active,
    &:active:focus {
      color: #211f58;
      font-size: 15px;
      font-weight: 500;
      background-color: #fff;
      border: 1px solid #eff0f1;
      // box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
    }
  }

  .dropdown-toggle::after {
    display: none;
  }
}

.role-container {
  align-items: center;
  .role {
    color: #231f20;
    height: 34px;
    border: 1px solid #eff0f1;
    border-radius: 100px;
    font-size: 11px;
    line-height: 14.3px;
    background-color: #ffffff;
  }
}

.border-none {
  border: none !important;
}

.vue-recycle-scroller {
  &.scroller {
    height: 151px;
  }
}

@media (max-width: 420px) {
  .role-inner {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
